import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';

const DepartmentFormModal = ({ open, onClose, onSubmit, initialData, isEdit, loading }) => {
  const [formData, setFormData] = useState({
    DepartmentCode: '',
    DepartmentName: '',
    IsActive: true,
    CreatedBy: '', // Will be set to actual user ID
    UpdatedBy: ''  // Will be set to actual user ID
  });
  
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  // Fetch current user when component mounts
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          console.error('No authentication token found');
          return;
        }
        
        const response = await fetch('/api/current-user', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const userData = await response.json();
          if (userData.user && userData.user.UserID) {
            setCurrentUser(userData.user);
            console.log('Current user fetched:', userData.user);
          } else {
            console.error('Invalid user data received:', userData);
          }
        } else {
          console.error('Failed to fetch current user:', response.status);
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
      } finally {
        setLoadingUser(false);
      }
    };
    
    if (open) {
      fetchCurrentUser();
    }
  }, [open]);

  useEffect(() => {
    if (isEdit && initialData && currentUser) {
      // Fetch department details if editing
      const fetchDepartment = async () => {
        try {
          const response = await fetch(`/api/departments/${initialData}`);
          if (response.ok) {
            const data = await response.json();
            setFormData({
              DepartmentCode: data.DepartmentCode,
              DepartmentName: data.DepartmentName,
              IsActive: data.IsActive,
              CreatedBy: data.CreatedBy || '', // Keep existing CreatedBy
              UpdatedBy: currentUser.UserID.toString() // Set to current user for update
            });
          }
        } catch (error) {
          console.error('Error fetching department:', error);
        }
      };
      fetchDepartment();
    } else if (currentUser) {
      // Reset form for new department
      setFormData({
        DepartmentCode: '',
        DepartmentName: '',
        IsActive: true,
        CreatedBy: currentUser.UserID.toString(), // Set to current user
        UpdatedBy: '' // Not needed for create
      });
    }
  }, [initialData, isEdit, open, currentUser]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Ensure we have a valid user ID before submitting
    if (!currentUser || !currentUser.UserID) {
      alert('User information not available. Please refresh the page and try again.');
      return;
    }
    
    // Make sure we have the correct user ID for the operation
    const submitData = {
      ...formData
    };
    
    if (isEdit) {
      submitData.UpdatedBy = currentUser.UserID.toString();
    } else {
      submitData.CreatedBy = currentUser.UserID.toString();
    }
    
    console.log('Submitting department data:', submitData);
    onSubmit(submitData);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              {isEdit ? 'Edit Department' : 'Create New Department'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <FaTimes className="text-xl" />
            </button>
          </div>

          {loadingUser ? (
            <div className="flex justify-center items-center h-40">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading user information...</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Department Code *
                  </label>
                  <input
                    type="text"
                    name="DepartmentCode"
                    value={formData.DepartmentCode}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2c3e50]"
                    required
                    disabled={isEdit} // Department code should not be editable after creation
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {isEdit ? "Department code cannot be changed" : "Enter a unique department code"}
                  </p>
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Department Name *
                  </label>
                  <input
                    type="text"
                    name="DepartmentName"
                    value={formData.DepartmentName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2c3e50]"
                    required
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="IsActive"
                    checked={formData.IsActive}
                    onChange={handleChange}
                    className="h-5 w-5 text-[#2c3e50] focus:ring-[#2c3e50] border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-gray-700">
                    Active
                  </label>
                </div>

                {/* Debug info - can be removed in production */}
                {/* <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded">
                  <div>Current User ID: {currentUser ? currentUser.UserID : 'Not available'}</div>
                  <div>Created By: {formData.CreatedBy}</div>
                  <div>Updated By: {formData.UpdatedBy}</div>
                </div> */}
              </div>

              <div className="mt-8 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || loadingUser || !currentUser}
                  className="px-4 py-2 bg-[#2c3e50] text-white rounded-lg hover:bg-[#34495e] transition-colors disabled:opacity-50"
                >
                  {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default DepartmentFormModal;